// src/components/SavedReports.js
import React, { useState, useEffect, useRef } from 'react';
import { api } from '../services/api';

// ─── Viewport hook ───
const useViewport = () => {
  const [width, setWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1280
  );
  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return {
    width,
    isMobile: width < 640,
    isTablet: width >= 640 && width < 1024,
    isDesktop: width >= 1024,
  };
};

const getInitials = (name) => {
  if (!name || name === 'Unknown Patient') return 'UN';
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .substring(0, 2)
    .toUpperCase();
};

const formatEnumString = (enumStr) => {
  if (!enumStr) return 'General';
  return enumStr
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

// ─── Icons (Feather-style SVGs, Lucide-compatible) ───
const Icons = {
  Bookmark: ({ size = 24, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
    </svg>
  ),
  Shield: ({ size = 28, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  ),
  FileText: ({ size = 18, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
    </svg>
  ),
  Eye: ({ size = 20, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ),
  Download: ({ size = 20, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  ),
  MoreVertical: ({ size = 20, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="1" />
      <circle cx="12" cy="5" r="1" />
      <circle cx="12" cy="19" r="1" />
    </svg>
  ),
  Share2: ({ size = 16, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="18" cy="5" r="3" />
      <circle cx="6" cy="12" r="3" />
      <circle cx="18" cy="19" r="3" />
      <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
      <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
    </svg>
  ),
  Trash: ({ size = 16, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </svg>
  ),
  Printer: ({ size = 16, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 9V2h12v7M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2" />
      <rect x="6" y="14" width="12" height="8" />
    </svg>
  ),
  PlusSquare: ({ size = 18, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
      <line x1="12" y1="8" x2="12" y2="16" />
      <line x1="8" y1="12" x2="16" y2="12" />
    </svg>
  ),
  Upload: ({ size = 18, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  ),
  FolderOpen: ({ size = 18, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
    </svg>
  ),
  Calendar: ({ size = 18, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  ),
  Pencil: ({ size = 18, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  ),
  CheckCircle: ({ size = 18, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  ),
  ChevronRight: ({ size = 16, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  ),
  X: ({ size = 24, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
  Filter: ({ size = 18, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
    </svg>
  ),
};

const SavedReports = ({ userProfile, onBack, onNavigate }) => {
  const { isMobile, isTablet } = useViewport();

  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [previewReport, setPreviewReport] = useState(null);

  const fileInputRef = useRef(null);

  useEffect(() => {
    const workspace = document.querySelector('.workspace');
    if (workspace) workspace.scrollTo({ top: 0, behavior: 'smooth' });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const fetchReports = async () => {
    try {
      setLoading(true);
      const data = await api.searchReports({ query: '', size: 50 });

      const mappedReports = (
        Array.isArray(data) ? data : data?.content || []
      ).map((r) => ({
        id: r.id,
        scanType: r.scanType || 'GENERAL',
        reportType: r.reportType || 'NORMAL',
        clinicalHistory:
          r.patientName || r.patientId || r.clinicalHistory || 'Unknown Patient',
        patientId:
          r.patientId ||
          `PID: ${Math.floor(Math.random() * 900000) + 100000}`,
        age: r.patientAge,
        sex: r.patientSex,
        date:
          r.scanDate ||
          new Date(r.createdAt || Date.now()).toISOString().split('T')[0],
        time: '10:24 AM',
        doctor: r.signatoryName || 'Dr. Smith',
        findings: r.findings,
        impression: r.impression,
        recommendation: r.recommendation,
        status:
          r.status || (Math.random() > 0.8 ? 'Draft' : 'Completed'),
      }));

      setReports(mappedReports);
    } catch (error) {
      console.error('Error fetching reports:', error);
      setReports([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userProfile) {
      fetchReports();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown-container')) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () =>
      document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleDelete = async (reportId) => {
    if (window.confirm('Are you sure you want to delete this report?')) {
      try {
        setLoading(true);
        await api.deleteReport(reportId);
        setActiveDropdown(null);
        fetchReports();
      } catch (error) {
        setLoading(false);
        alert('Failed to delete report: ' + error.message);
      }
    }
  };

  const handlePrint = (report) => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Print Report</title>
          <style>
            body { font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; padding: 40px; color: #333; max-width: 800px; margin: 0 auto; }
            h1 { color: #0f172a; margin-bottom: 5px; letter-spacing: 1px; }
            h2 { text-align: center; color: #3b82f6; margin-top: 0; font-size: 1.2rem; text-transform: uppercase; letter-spacing: 2px; }
            .section { margin-bottom: 25px; }
            h3 { font-size: 1.1rem; color: #1e293b; border-bottom: 2px solid #e2e8f0; padding-bottom: 5px; margin-bottom: 10px; text-transform: uppercase; }
            .label { font-weight: 600; color: #475569; }
            .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; background: #f8fafc; padding: 15px; border-radius: 8px; border: 1px solid #e2e8f0; }
            .info-item { margin: 0; }
            .content-text { line-height: 1.6; color: #334155; }
          </style>
        </head>
        <body>
          <div style="display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #1e293b; padding-bottom: 15px; margin-bottom: 20px;">
             <div>
                <h1 style="margin: 0; color: #1e293b; font-size: 18pt; font-weight: 800;">Echion Health</h1>
                <p style="margin: 0; color: #64748b; font-size: 9pt; letter-spacing: 1px; text-transform: uppercase;">Sonoscribe Diagnostics</p>
             </div>
             <div style="text-align: right; font-size: 9pt; color: #334155; line-height: 1.5;">
                <div style="font-weight: bold; color: #1e293b;">Contact Information</div>
                <div>+054-843-2033</div>
                <div style="color: #2563eb;">www.echionhealth.com</div>
             </div>
          </div>

          <h2 style="color: #1e293b;">ULTRASOUND ${formatEnumString(report.scanType).toUpperCase()} REPORT</h2>

          <div class="section info-grid">
            <p class="info-item"><span class="label">Patient Name:</span> ${report.clinicalHistory}</p>
            <p class="info-item"><span class="label">Patient ID:</span> ${report.patientId}</p>
            <p class="info-item"><span class="label">Age/Sex:</span> ${report.age || 'N/A'} yrs / ${formatEnumString(report.sex) || 'N/A'}</p>
            <p class="info-item"><span class="label">Date of Scan:</span> ${report.date}</p>
            <p class="info-item"><span class="label">Referring Dr:</span> ${report.doctor}</p>
          </div>

          <div class="section">
            <h3>Sonographic Findings</h3>
            <div class="content-text">${report.findings ? report.findings.replace(/\n/g, '<br/>') : 'No specific findings recorded.'}</div>
          </div>

          <div class="section">
            <h3>Impression</h3>
            <p class="content-text" style="font-weight: 600;">${report.impression || 'No impression recorded.'}</p>
          </div>

          <div class="section">
            <h3>Recommendation</h3>
            <p class="content-text">${report.recommendation || 'None.'}</p>
          </div>

          <div style="display: flex; justify-content: flex-end; margin-top: 30px; padding-top: 20px;">
              <div style="text-align: center; width: 220px;">
                  <div style="height: 50px; display: flex; align-items: center; justify-content: center;">
                    <span style="font-family: 'Brush Script MT', 'Cedarville Cursive', cursive; font-size: 20pt; color: #1e293b;">${report.doctor}</span>
                  </div>
                <div style="border-top: 1px solid #000; padding-top: 5px; margin-top: 5px;">
                  <strong style="display: block; font-size: 10pt; color: #000;">${report.doctor}</strong>
                  <span style="font-size: 9pt; color: #475569;">Consultant Radiologist</span>
                </div>
              </div>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 500);
  };

  const handleImportReport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      await api.uploadVaultDocument(file);
      alert(`Report document "${file.name}" has been successfully imported!`);
      fetchReports();
    } catch (err) {
      alert(`Import failed: ${err.message}`);
    } finally {
      e.target.value = null;
    }
  };

  const handleBulkExport = () => {
    if (reports.length === 0) {
      alert('No reports available to export.');
      return;
    }
    alert(`Initiating secure bulk export of ${reports.length} reports...`);
  };

  const filteredReports = reports.filter((report) => {
    const q = searchQuery.toLowerCase();
    const typeEnum = (report.scanType || '').toLowerCase();
    const typeReadable = formatEnumString(report.scanType).toLowerCase();
    const name = (report.clinicalHistory || '').toLowerCase();
    return (
      typeEnum.includes(q) || typeReadable.includes(q) || name.includes(q)
    );
  });

  const totalReports = reports.length;
  const completedReports = reports.filter(
    (r) => r.status === 'Completed'
  ).length;
  const draftReports = reports.filter((r) => r.status === 'Draft').length;

  const getCategoryCount = (categoryKey) => {
    return reports.filter((r) => {
      const type = r.scanType || '';
      switch (categoryKey) {
        case 'abdomen':
          return type === 'ABDOMINAL';
        case 'abd_pel_male':
          return type === 'ABDOMEN_PELVIS_MALE';
        case 'abd_pel_female':
          return type === 'ABDOMEN_PELVIS_FEMALE';
        case 'obs':
          return (
            type.includes('OBSTETRIC') ||
            type === 'ANOMALY' ||
            type === 'BIOPHYSICAL_PROFILE'
          );
        case 'breast':
          return type === 'BREAST';
        case 'scrotal':
          return type === 'SCROTAL';
        case 'transvaginal':
          return type === 'TRANSVAGINAL' || type === 'PELVIC_FEMALE';
        case 'doppler':
          return type.includes('DOPPLER');
        case 'msk':
          return type === 'MUSCULOSKELETAL';
        case 'general':
          return (
            type === 'GENERAL' ||
            type === 'CHEST' ||
            type === 'NECK' ||
            type === 'THYROID' ||
            type === 'NEONATAL_HEAD' ||
            type.includes('ECHO') ||
            type === 'PELVIC_MALE' ||
            type === 'PENILE' ||
            type === 'TRANSABDOMINAL_PELVIC'
          );
        default:
          return false;
      }
    }).length;
  };

  const reportCategories = [
    { name: 'Abdomen', searchKey: 'ABDOMINAL', count: getCategoryCount('abdomen'), color: '#3b82f6' },
    { name: 'Abd. & Pelvis (M)', searchKey: 'ABDOMEN_PELVIS_MALE', count: getCategoryCount('abd_pel_male'), color: '#06b6d4' },
    { name: 'Abd. & Pelvis (F)', searchKey: 'ABDOMEN_PELVIS_FEMALE', count: getCategoryCount('abd_pel_female'), color: '#0ea5e9' },
    { name: 'OBS', desc: 'Obstetrics', searchKey: 'OBSTETRIC', count: getCategoryCount('obs'), color: '#ec4899' },
    { name: 'Breast', searchKey: 'BREAST', count: getCategoryCount('breast'), color: '#f43f5e' },
    { name: 'Scrotal', searchKey: 'SCROTAL', count: getCategoryCount('scrotal'), color: '#8b5cf6' },
    { name: 'Transvaginal', searchKey: 'TRANSVAGINAL', count: getCategoryCount('transvaginal'), color: '#d946ef' },
    { name: 'Doppler', desc: 'Vascular', searchKey: 'DOPPLER', count: getCategoryCount('doppler'), color: '#f97316' },
    { name: 'MSK', desc: 'Musculoskeletal', searchKey: 'MUSCULOSKELETAL', count: getCategoryCount('msk'), color: '#10b981' },
    { name: 'General', searchKey: '', count: getCategoryCount('general'), color: '#64748b' },
  ];

  // Responsive grid for category cards
  const categoryCols = isMobile
    ? 'repeat(2, 1fr)'
    : isTablet
    ? 'repeat(3, 1fr)'
    : 'repeat(5, 1fr)';

  return (
    <>
      <style>{`
        .saved-reports-page { background-color: #f8fafc; min-height: 100vh; font-family: 'Inter', sans-serif; color: #0f172a; padding: 24px 20px; box-sizing: border-box; }
        @media (min-width: 1024px) { .saved-reports-page { padding: 30px 40px; } }

        .header-top { display: flex; justify-content: space-between; align-items: center; margin-bottom: 24px; gap: 16px; flex-wrap: wrap; }
        .header-title-container { display: flex; align-items: center; gap: 15px; }
        .header-icon { width: 44px; height: 44px; background: #eff6ff; border-radius: 12px; display: flex; align-items: center; justify-content: center; color: #3b82f6; flex-shrink: 0; }
        .header-title { margin: 0; font-size: 1.6rem; font-weight: 700; }
        @media (min-width: 1024px) { .header-title { font-size: 1.8rem; } }
        .header-subtitle { margin: 4px 0 0 0; color: #64748b; font-size: 0.9rem; }
        .filter-group { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; }
        .filter-btn { background: white; border: 1px solid #e2e8f0; padding: 8px 14px; border-radius: 8px; color: #475569; font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 6px; font-size: 0.85rem; transition: all 0.15s; }
        .filter-btn:hover { background: #f8fafc; border-color: #cbd5e1; }

        .main-layout { display: flex; gap: 24px; }
        @media (max-width: 1023px) { .main-layout { flex-direction: column; } }
        .left-content { flex: 1; min-width: 0; }
        .right-sidebar { width: 320px; flex-shrink: 0; display: flex; flex-direction: column; gap: 24px; }
        @media (max-width: 1023px) { .right-sidebar { width: 100%; } }

        .section-title { font-size: 1.05rem; font-weight: 700; margin: 0 0 15px 0; color: #1e293b; }
        .category-grid { display: grid; gap: 12px; margin-bottom: 32px; }
        .category-card { background: white; border-radius: 14px; padding: 16px 12px; display: flex; flex-direction: column; align-items: center; text-align: center; box-shadow: 0 2px 10px rgba(0,0,0,0.02); border: 1px solid #f1f5f9; cursor: pointer; transition: transform 0.15s, box-shadow 0.15s; min-width: 0; }
        .category-card:hover { transform: translateY(-2px); box-shadow: 0 8px 20px rgba(0,0,0,0.06); }
        .cat-icon-box { width: 48px; height: 48px; border-radius: 12px; margin-bottom: 10px; display: flex; align-items: center; justify-content: center; }
        .cat-title { font-weight: 700; font-size: 0.85rem; margin-bottom: 2px; line-height: 1.2; word-break: break-word; }
        .cat-desc { font-size: 0.7rem; color: #94a3b8; margin-bottom: 8px; height: 12px; }
        .cat-badge { background: #f1f5f9; color: #64748b; font-size: 0.7rem; font-weight: 600; padding: 3px 9px; border-radius: 20px; white-space: nowrap; }

        .reports-table-container { background: white; border-radius: 14px; border: 1px solid #f1f5f9; overflow-x: auto; -webkit-overflow-scrolling: touch; }
        .reports-table { width: 100%; border-collapse: collapse; min-width: 700px; }
        .reports-table th { text-align: left; padding: 14px 16px; color: #64748b; font-weight: 600; font-size: 0.8rem; background: #f8fafc; border-bottom: 1px solid #f1f5f9; text-transform: uppercase; letter-spacing: 0.3px; }
        .reports-table td { padding: 14px 16px; border-bottom: 1px solid #f1f5f9; vertical-align: middle; }
        .reports-table tr:last-child td { border-bottom: none; }
        .patient-cell { display: flex; align-items: center; gap: 12px; }
        .avatar { width: 38px; height: 38px; border-radius: 50%; background: #e2e8f0; display: flex; align-items: center; justify-content: center; font-weight: 700; color: #475569; font-size: 0.8rem; flex-shrink: 0; }
        .patient-name { font-weight: 600; font-size: 0.9rem; margin-bottom: 2px; white-space: nowrap; }
        .patient-id { color: #94a3b8; font-size: 0.75rem; white-space: nowrap; }
        .type-cell { display: flex; align-items: center; gap: 10px; }
        .type-icon { width: 30px; height: 30px; border-radius: 8px; background: #eff6ff; display: flex; align-items: center; justify-content: center; color: #3b82f6; flex-shrink: 0; }
        .status-pill { padding: 4px 10px; border-radius: 20px; font-size: 0.7rem; font-weight: 700; display: inline-block; text-transform: uppercase; letter-spacing: 0.3px; }
        .status-completed { background: #ecfdf5; color: #10b981; }
        .status-draft { background: #f1f5f9; color: #64748b; }
        .action-btns { display: flex; gap: 6px; align-items: center; color: #64748b; position: relative; }
        .action-btn { background: none; border: none; cursor: pointer; color: inherit; padding: 6px; transition: color 0.15s, background 0.15s; border-radius: 6px; display: flex; align-items: center; justify-content: center; }
        .action-btn:hover { color: #0f172a; background: #f1f5f9; }
        .dropdown-menu { position: absolute; right: 0; top: 100%; margin-top: 4px; background: white; border: 1px solid #e2e8f0; border-radius: 8px; box-shadow: 0 4px 15px rgba(0,0,0,0.1); width: 160px; z-index: 20; display: flex; flex-direction: column; overflow: hidden; }
        .dropdown-item { padding: 10px 14px; text-align: left; background: none; border: none; font-size: 0.85rem; color: #475569; cursor: pointer; display: flex; align-items: center; gap: 8px; font-family: inherit; }
        .dropdown-item:hover { background: #f8fafc; color: #0f172a; }
        .dropdown-item.danger { color: #ef4444; }
        .dropdown-item.danger:hover { background: #fef2f2; color: #dc2626; }

        .side-card { background: white; border-radius: 14px; border: 1px solid #f1f5f9; padding: 18px; }
        .action-list-item { display: flex; align-items: center; justify-content: space-between; padding: 12px 14px; border: 1px solid #f1f5f9; border-radius: 10px; margin-bottom: 10px; cursor: pointer; transition: background 0.15s; }
        .action-list-item:last-child { margin-bottom: 0; }
        .action-list-item:hover { background: #f8fafc; }
        .action-left { display: flex; align-items: center; gap: 12px; min-width: 0; }
        .action-icon-bg { width: 34px; height: 34px; border-radius: 8px; background: #eff6ff; display: flex; align-items: center; justify-content: center; color: #3b82f6; flex-shrink: 0; }
        .action-title { font-weight: 600; font-size: 0.87rem; color: #1e293b; margin: 0; }
        .action-subtitle { font-size: 0.72rem; color: #94a3b8; margin: 0; }
        .stat-row { display: flex; justify-content: space-between; align-items: center; padding: 12px 0; border-bottom: 1px solid #f1f5f9; }
        .stat-row:last-child { border-bottom: none; padding-bottom: 0; }
        .stat-label-group { display: flex; align-items: center; gap: 10px; color: #475569; font-weight: 500; font-size: 0.9rem; }
        .stat-val { font-weight: 700; font-size: 1rem; color: #0f172a; }

        .preview-modal-overlay { position: fixed; inset: 0; background: rgba(15, 23, 42, 0.7); backdrop-filter: blur(2px); z-index: 9999; display: flex; align-items: center; justify-content: center; padding: 16px; box-sizing: border-box; }
        .preview-modal-content { background: white; border-radius: 12px; width: 800px; max-width: 100%; max-height: 92vh; display: flex; flex-direction: column; overflow: hidden; box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1); }
        .preview-modal-header { padding: 16px 20px; border-bottom: 1px solid #e2e8f0; display: flex; justify-content: space-between; align-items: center; background: #f8fafc; gap: 12px; }
        .preview-modal-body { padding: 20px; overflow-y: auto; font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; }
        .report-preview-sheet { border: 1px solid #e2e8f0; padding: 24px; border-radius: 8px; background: white; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05); }
        .preview-footer { padding: 12px 20px; border-top: 1px solid #e2e8f0; display: flex; justify-content: flex-end; gap: 10px; background: #f8fafc; flex-wrap: wrap; }

        .glass-loader-overlay { position: fixed; inset: 0; background: rgba(255, 255, 255, 0.7); backdrop-filter: blur(8px); z-index: 9999; display: flex; align-items: center; justify-content: center; }
        .running-wrapper { display: flex; flex-direction: column; align-items: center; gap: 24px; background: white; padding: 32px 48px; border-radius: 20px; box-shadow: 0 10px 40px rgba(0,0,0,0.08); border: 1px solid rgba(255,255,255,0.5); }
        .running-track { width: 60px; height: 60px; border-radius: 50%; border: 4px solid #e2e8f0; position: relative; animation: spin 1.2s linear infinite; }
        .running-object { position: absolute; top: -6px; left: calc(50% - 6px); width: 12px; height: 12px; background: #3b82f6; border-radius: 50%; box-shadow: 0 0 12px #3b82f6; }
        @keyframes spin { to { transform: rotate(360deg); } }
        .pulse-text { font-weight: 700; color: #3b82f6; font-size: 1rem; letter-spacing: 0.4px; animation: pulse 1.5s ease-in-out infinite; }
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
      `}</style>

      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept=".pdf,.doc,.docx,.txt"
        onChange={handleImportReport}
      />

      {loading && (
        <div className="glass-loader-overlay fade-in">
          <div className="running-wrapper">
            <div className="running-track">
              <div className="running-object"></div>
            </div>
            <div className="pulse-text">Loading Reports…</div>
          </div>
        </div>
      )}

      {/* Preview modal */}
      {previewReport && (
        <div
          className="preview-modal-overlay"
          onClick={() => setPreviewReport(null)}
        >
          <div
            className="preview-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="preview-modal-header">
              <div style={{ minWidth: 0 }}>
                <h3
                  style={{
                    margin: 0,
                    fontSize: '1.1rem',
                    color: '#0f172a',
                  }}
                >
                  Report Preview
                </h3>
                <p
                  style={{
                    margin: '4px 0 0 0',
                    fontSize: '0.82rem',
                    color: '#64748b',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {previewReport.patientId} – {previewReport.clinicalHistory}
                </p>
              </div>
              <button
                onClick={() => setPreviewReport(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#64748b',
                  display: 'flex',
                  padding: 4,
                }}
                aria-label="Close preview"
              >
                <Icons.X size={22} />
              </button>
            </div>

            <div className="preview-modal-body">
              <div className="report-preview-sheet">
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    borderBottom: '2px solid #1e293b',
                    paddingBottom: '15px',
                    marginBottom: '20px',
                    gap: 12,
                    flexWrap: 'wrap',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '15px',
                    }}
                  >
                    <img
                      src={process.env.PUBLIC_URL + '/echionLOGO.png'}
                      alt="Logo"
                      style={{
                        width: '44px',
                        height: '44px',
                        objectFit: 'contain',
                      }}
                    />
                    <div>
                      <h1
                        style={{
                          margin: 0,
                          color: '#1e293b',
                          fontSize: '16pt',
                          fontWeight: '800',
                          letterSpacing: '0.4px',
                        }}
                      >
                        Echion Health
                      </h1>
                      <p
                        style={{
                          margin: 0,
                          color: '#64748b',
                          fontSize: '8.5pt',
                          letterSpacing: '1px',
                          textTransform: 'uppercase',
                        }}
                      >
                        Sonoscribe Diagnostics
                      </p>
                    </div>
                  </div>
                  <div
                    style={{
                      textAlign: 'right',
                      fontSize: '8.5pt',
                      color: '#334155',
                      lineHeight: '1.5',
                    }}
                  >
                    <div
                      style={{ fontWeight: 'bold', color: '#1e293b' }}
                    >
                      Contact
                    </div>
                    <div>+054-843-2033</div>
                    <div style={{ color: '#2563eb' }}>
                      www.echionhealth.com
                    </div>
                  </div>
                </div>

                <h2
                  style={{
                    textAlign: 'center',
                    margin: '0 0 20px 0',
                    fontSize: '11pt',
                    letterSpacing: '1.4px',
                    color: '#1e293b',
                    textTransform: 'uppercase',
                    fontWeight: 'bold',
                  }}
                >
                  ULTRASOUND{' '}
                  {formatEnumString(previewReport.scanType).toUpperCase()}{' '}
                  REPORT
                </h2>

                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '10px',
                    background: '#f8fafc',
                    padding: '15px',
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0',
                    marginBottom: '25px',
                    fontSize: '9.5pt',
                  }}
                >
                  <p style={{ margin: 0 }}>
                    <span
                      style={{ fontWeight: 600, color: '#475569' }}
                    >
                      Patient:
                    </span>{' '}
                    {previewReport.clinicalHistory}
                  </p>
                  <p style={{ margin: 0 }}>
                    <span
                      style={{ fontWeight: 600, color: '#475569' }}
                    >
                      Patient ID:
                    </span>{' '}
                    {previewReport.patientId}
                  </p>
                  <p style={{ margin: 0 }}>
                    <span
                      style={{ fontWeight: 600, color: '#475569' }}
                    >
                      Age / Sex:
                    </span>{' '}
                    {previewReport.age || 'N/A'} yrs /{' '}
                    {formatEnumString(previewReport.sex) || 'N/A'}
                  </p>
                  <p style={{ margin: 0 }}>
                    <span
                      style={{ fontWeight: 600, color: '#475569' }}
                    >
                      Scan Date:
                    </span>{' '}
                    {previewReport.date}
                  </p>
                  <p style={{ margin: 0 }}>
                    <span
                      style={{ fontWeight: 600, color: '#475569' }}
                    >
                      Referring Dr:
                    </span>{' '}
                    {previewReport.doctor}
                  </p>
                </div>

                <div style={{ marginBottom: '25px' }}>
                  <h3
                    style={{
                      fontSize: '0.95rem',
                      color: '#1e293b',
                      borderBottom: '2px solid #e2e8f0',
                      paddingBottom: '5px',
                      marginBottom: '10px',
                      textTransform: 'uppercase',
                    }}
                  >
                    Sonographic Findings
                  </h3>
                  <div
                    style={{ lineHeight: 1.6, color: '#334155', fontSize: '9.5pt' }}
                    dangerouslySetInnerHTML={{
                      __html: previewReport.findings
                        ? previewReport.findings.replace(/\n/g, '<br/>')
                        : 'No specific findings recorded.',
                    }}
                  ></div>
                </div>

                <div style={{ marginBottom: '25px' }}>
                  <h3
                    style={{
                      fontSize: '0.95rem',
                      color: '#1e293b',
                      borderBottom: '2px solid #e2e8f0',
                      paddingBottom: '5px',
                      marginBottom: '10px',
                      textTransform: 'uppercase',
                    }}
                  >
                    Impression
                  </h3>
                  <p
                    style={{
                      lineHeight: 1.6,
                      color: '#334155',
                      fontWeight: 600,
                      fontSize: '9.5pt',
                    }}
                  >
                    {previewReport.impression || 'No impression recorded.'}
                  </p>
                </div>

                <div style={{ marginBottom: '25px' }}>
                  <h3
                    style={{
                      fontSize: '0.95rem',
                      color: '#1e293b',
                      borderBottom: '2px solid #e2e8f0',
                      paddingBottom: '5px',
                      marginBottom: '10px',
                      textTransform: 'uppercase',
                    }}
                  >
                    Recommendation
                  </h3>
                  <p
                    style={{
                      lineHeight: 1.6,
                      color: '#334155',
                      fontSize: '9.5pt',
                    }}
                  >
                    {previewReport.recommendation || 'None.'}
                  </p>
                </div>

                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    marginTop: '30px',
                    paddingTop: '20px',
                  }}
                >
                  <div style={{ textAlign: 'center', width: 220 }}>
                    <div
                      style={{
                        height: 50,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <span
                        style={{
                          fontFamily:
                            "'Brush Script MT', 'Cedarville Cursive', cursive",
                          fontSize: '20pt',
                          color: '#1e293b',
                        }}
                      >
                        {previewReport.doctor}
                      </span>
                    </div>
                    <div
                      style={{
                        borderTop: '1px solid #000',
                        paddingTop: '5px',
                        margin: '5px 0 0 0',
                      }}
                    >
                      <strong
                        style={{
                          display: 'block',
                          fontSize: '10pt',
                          color: '#000',
                        }}
                      >
                        {previewReport.doctor}
                      </strong>
                      <span
                        style={{ fontSize: '9pt', color: '#475569' }}
                      >
                        Consultant Radiologist
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="preview-footer">
              <button
                onClick={() => setPreviewReport(null)}
                style={{
                  padding: '9px 18px',
                  borderRadius: 6,
                  border: '1px solid #cbd5e1',
                  background: 'white',
                  color: '#475569',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontSize: '0.88rem',
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => handlePrint(previewReport)}
                style={{
                  padding: '9px 18px',
                  borderRadius: 6,
                  border: 'none',
                  background: '#3b82f6',
                  color: 'white',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  fontSize: '0.88rem',
                }}
              >
                <Icons.Printer size={16} color="#ffffff" />
                Print Document
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="saved-reports-page">
        <div className="header-top">
          <div className="header-title-container">
            <div className="header-icon">
              <Icons.Bookmark size={22} />
            </div>
            <div>
              <h1 className="header-title">Saved Reports</h1>
              <p className="header-subtitle">
                Browse and manage your saved ultrasound reports
              </p>
            </div>
          </div>
          <div className="filter-group">
            <button className="filter-btn">
              <Icons.Calendar size={14} /> All Dates
            </button>
            <button className="filter-btn">
              <Icons.Filter size={14} /> By Date
            </button>
            <button className="filter-btn">
              <Icons.FileText size={14} /> By File
            </button>
          </div>
        </div>

        <div className="main-layout">
          <div className="left-content">
            <h3 className="section-title">Select Report Type</h3>

            <div
              className="category-grid"
              style={{ gridTemplateColumns: categoryCols }}
            >
              {reportCategories.map((cat, idx) => (
                <div
                  key={idx}
                  className="category-card"
                  onClick={() => setSearchQuery(cat.searchKey)}
                >
                  <div
                    className="cat-icon-box"
                    style={{ backgroundColor: cat.color }}
                  >
                    <Icons.Shield size={26} color="#ffffff" />
                  </div>
                  <div className="cat-title">{cat.name}</div>
                  <div className="cat-desc">{cat.desc || ''}</div>
                  <div className="cat-badge">
                    {cat.count} report{cat.count === 1 ? '' : 's'}
                  </div>
                </div>
              ))}
            </div>

            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 15,
                gap: 12,
                flexWrap: 'wrap',
              }}
            >
              <h3 className="section-title" style={{ margin: 0 }}>
                Recent Reports
              </h3>
              <input
                type="text"
                placeholder="Search table..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  padding: '8px 14px',
                  borderRadius: 20,
                  border: '1px solid #cbd5e1',
                  fontSize: '0.88rem',
                  width: isMobile ? '100%' : 250,
                  outlineColor: '#3b82f6',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            <div className="reports-table-container">
              <table className="reports-table">
                <thead>
                  <tr>
                    <th>Patient</th>
                    <th>Report Type</th>
                    <th>Date</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredReports.length > 0 ? (
                    filteredReports.map((report) => (
                      <tr key={report.id}>
                        <td>
                          <div className="patient-cell">
                            <div className="avatar">
                              {getInitials(report.clinicalHistory)}
                            </div>
                            <div style={{ minWidth: 0 }}>
                              <div className="patient-name">
                                {report.clinicalHistory}
                              </div>
                              <div className="patient-id">
                                {report.patientId}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="type-cell">
                            <div className="type-icon">
                              <Icons.FileText size={16} />
                            </div>
                            <div style={{ minWidth: 0 }}>
                              <div className="patient-name">
                                {formatEnumString(report.scanType)}
                              </div>
                              <div className="patient-id">
                                {formatEnumString(report.reportType)}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="patient-name">{report.date}</div>
                          <div className="patient-id">{report.doctor}</div>
                        </td>
                        <td>
                          <span
                            className={`status-pill ${
                              report.status === 'Completed'
                                ? 'status-completed'
                                : 'status-draft'
                            }`}
                          >
                            {report.status}
                          </span>
                        </td>
                        <td>
                          <div className="action-btns">
                            <button
                              className="action-btn"
                              title="Preview report"
                              onClick={() => setPreviewReport(report)}
                            >
                              <Icons.Eye size={18} />
                            </button>
                            <button
                              className="action-btn"
                              title="Download / Print"
                              onClick={() => handlePrint(report)}
                            >
                              <Icons.Download size={18} />
                            </button>

                            <div
                              className="dropdown-container"
                              style={{ position: 'relative' }}
                            >
                              <button
                                className="action-btn"
                                title="More options"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setActiveDropdown(
                                    activeDropdown === report.id
                                      ? null
                                      : report.id
                                  );
                                }}
                              >
                                <Icons.MoreVertical size={18} />
                              </button>

                              {activeDropdown === report.id && (
                                <div className="dropdown-menu">
                                  <button
                                    className="dropdown-item"
                                    onClick={() => {
                                      setActiveDropdown(null);
                                      handlePrint(report);
                                    }}
                                  >
                                    <Icons.Share2 size={14} />
                                    Share Report
                                  </button>
                                  <button
                                    className="dropdown-item danger"
                                    onClick={() => handleDelete(report.id)}
                                  >
                                    <Icons.Trash size={14} />
                                    Delete
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan="5"
                        style={{
                          textAlign: 'center',
                          padding: 40,
                          color: '#94a3b8',
                        }}
                      >
                        No reports found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="right-sidebar">
            <div className="side-card">
              <h3 className="section-title">Quick Actions</h3>

              <div
                className="action-list-item"
                onClick={() => onNavigate && onNavigate('report')}
              >
                <div className="action-left">
                  <div className="action-icon-bg">
                    <Icons.PlusSquare size={16} />
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <h4 className="action-title">Create New Report</h4>
                    <p className="action-subtitle">
                      Start a new ultrasound report
                    </p>
                  </div>
                </div>
                <Icons.ChevronRight size={16} color="#94a3b8" />
              </div>

              <div
                className="action-list-item"
                onClick={() =>
                  fileInputRef.current && fileInputRef.current.click()
                }
              >
                <div className="action-left">
                  <div className="action-icon-bg">
                    <Icons.Upload size={16} />
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <h4 className="action-title">Import Report</h4>
                    <p className="action-subtitle">
                      Upload from file or device
                    </p>
                  </div>
                </div>
                <Icons.ChevronRight size={16} color="#94a3b8" />
              </div>

              <div className="action-list-item" onClick={handleBulkExport}>
                <div className="action-left">
                  <div className="action-icon-bg">
                    <Icons.Download size={16} />
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <h4 className="action-title">Bulk Export</h4>
                    <p className="action-subtitle">
                      Export multiple reports
                    </p>
                  </div>
                </div>
                <Icons.ChevronRight size={16} color="#94a3b8" />
              </div>

              <div
                className="action-list-item"
                onClick={() => onNavigate && onNavigate('vault')}
              >
                <div className="action-left">
                  <div className="action-icon-bg">
                    <Icons.FolderOpen size={16} />
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <h4 className="action-title">Sono Vault</h4>
                    <p className="action-subtitle">
                      Manage all your documents
                    </p>
                  </div>
                </div>
                <Icons.ChevronRight size={16} color="#94a3b8" />
              </div>
            </div>

            <div className="side-card">
              <h3 className="section-title">Report Stats</h3>

              <div className="stat-row">
                <div className="stat-label-group">
                  <Icons.FileText size={16} color="#3b82f6" />
                  Total Reports
                </div>
                <div className="stat-val">{totalReports}</div>
              </div>

              <div className="stat-row">
                <div className="stat-label-group">
                  <Icons.Calendar size={16} color="#64748b" />
                  This Month
                </div>
                <div className="stat-val">
                  {totalReports > 0
                    ? Math.max(1, Math.floor(totalReports * 0.3))
                    : 0}
                </div>
              </div>

              <div className="stat-row">
                <div className="stat-label-group">
                  <Icons.Pencil size={16} color="#f59e0b" />
                  Drafts
                </div>
                <div className="stat-val">{draftReports}</div>
              </div>

              <div className="stat-row">
                <div className="stat-label-group">
                  <Icons.CheckCircle size={16} color="#10b981" />
                  Completed
                </div>
                <div className="stat-val">{completedReports}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SavedReports;